import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { ChatExportStatus, ChatExportTask } from '../models/chat-export.model';

@Component({
  selector: 'servicenumber-chat-export-detail-modal',
  standalone: true,
  imports: [CommonModule, NzModalModule, NzButtonModule, NzTagModule, I18nPipe, TestIdDirective],
  template: `
    <nz-modal
      [nzVisible]="visible"
      [nzTitle]="modalTitle"
      [nzFooter]="modalFooter"
      (nzOnCancel)="onClose()"
      [testId]="'chat-export-detail-modal'"
    >
      <ng-template #modalTitle> {{ 'servicenumber.chat-export.detail-title' | i18n }}: {{ task?.id }} </ng-template>

      <ng-container *nzModalContent>
        <div class="detail-grid" *ngIf="task">
          <div class="detail-item">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.th-month' | i18n }}</span>
            <span class="detail-value font-medium">{{ task.month }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.th-status' | i18n }}</span>
            <span class="detail-value">
              <nz-tag [nzColor]="getStatusTagColor(task.status)">
                {{ getStatusTextKey(task.status) | i18n }}
              </nz-tag>
            </span>
          </div>
          <div class="detail-item" *ngIf="task.fileName">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.file-name' | i18n }}</span>
            <span class="detail-value font-medium text-break">{{ task.fileName }}</span>
          </div>
          <div class="detail-item" *ngIf="task.fileSize">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.file-size' | i18n }}</span>
            <span class="detail-value">{{ formatFileSize(task.fileSize) }}</span>
          </div>
          <div class="detail-item" *ngIf="task.totalMessages || task.totalRooms">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.th-stats' | i18n }}</span>
            <span class="detail-value">
              {{ task.totalMessages || 0 }} {{ 'servicenumber.chat-export.msg-unit' | i18n }} · {{ task.totalRooms || 0 }}
              {{ 'servicenumber.chat-export.room-unit' | i18n }}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.time-zone' | i18n }}</span>
            <span class="detail-value">{{ task.timeZone }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.th-task-owner' | i18n }}</span>
            <span class="detail-value">{{ formatOwnerName(task.ownerName) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.completed-time' | i18n }}</span>
            <span class="detail-value">{{ task.completedAt || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.expires-time' | i18n }}</span>
            <span class="detail-value">{{ task.expiresAt || '—' }}</span>
          </div>
          <div class="detail-item full-width" *ngIf="task.failureReason">
            <span class="detail-label muted">{{ 'servicenumber.chat-export.failure-reason' | i18n }}</span>
            <span class="detail-value text-danger">{{ task.failureReason }}</span>
          </div>
        </div>

        <div class="detail-note muted">
          {{ 'servicenumber.chat-export.detail-note' | i18n }}
        </div>
      </ng-container>

      <ng-template #modalFooter>
        <button nz-button nzType="default" (click)="onClose()" [testId]="'chat-export-detail-close-btn'">
          {{ 'common.close' | i18n }}
        </button>
      </ng-template>
    </nz-modal>
  `,
  styles: [
    `
      .detail-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-bottom: 20px;
      }
      .detail-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
        &.full-width {
          grid-column: 1 / -1;
        }
      }
      .detail-label {
        font-size: 13px;
      }
      .detail-value {
        font-size: 14px;
      }
      .muted {
        color: #67645f;
      }
      .font-medium {
        font-weight: 600;
      }
      .text-danger {
        color: #b4362b;
      }
      .detail-note {
        font-size: 13px;
        padding: 12px;
        background: #f9f8f7;
        border-radius: 8px;
        line-height: 1.5;
      }
    `
  ]
})
export class ChatExportDetailModalComponent {
  @Input() visible: boolean = false;
  @Input() task: ChatExportTask | null = null;
  @Output() readonly visibleChange = new EventEmitter<boolean>();

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  getStatusTagColor(status: ChatExportStatus): string {
    switch (status) {
      case ChatExportStatus.READY:
        return 'success';
      case ChatExportStatus.PROCESSING:
        return 'processing';
      case ChatExportStatus.FAILED:
        return 'error';
      case ChatExportStatus.EXPIRED:
      default:
        return 'default';
    }
  }

  getStatusTextKey(status: ChatExportStatus): string {
    switch (status) {
      case ChatExportStatus.READY:
        return 'servicenumber.chat-export.status.ready';
      case ChatExportStatus.PROCESSING:
        return 'servicenumber.chat-export.status.processing';
      case ChatExportStatus.FAILED:
        return 'servicenumber.chat-export.status.failed';
      case ChatExportStatus.EXPIRED:
      default:
        return 'servicenumber.chat-export.status.expired';
    }
  }

  formatFileSize(bytes?: number): string {
    if (bytes === undefined || bytes === null || bytes <= 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  formatOwnerName(name?: string): string {
    if (!name) return '管理員';
    const trimmed = name.trim();
    if (/^[0-9a-fA-F]{24}$/.test(trimmed)) {
      return '管理員';
    }
    return trimmed;
  }
}
